/**
 * ActivationAnalytics - Dashboard widget showing member activation metrics
 */
import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import apiClient from "../../utils/apiClient";
import { Activity, UserPlus } from "lucide-react";

function timeAgo(isoStr) {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function ActivationAnalytics({ orgId, onInviteClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await apiClient.get(`/orgs/${orgId}/activation-analytics`, { skipErrorToast: true });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load activation analytics:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-5 mb-8 animate-pulse" data-testid="activation-analytics-loading">
        <div className="h-4 bg-muted rounded w-40 mb-4" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 mb-8 flex items-center justify-between" data-testid="activation-analytics-error">
        <span className="text-sm text-muted-foreground">Failed to load activation data</span>
        <Button size="sm" variant="ghost" onClick={load} data-testid="activation-retry-btn">Retry</Button>
      </div>
    );
  }

  if (!data) return null;

  const pct = Math.round(data.activation_rate * 100);
  const noActivations = data.activated_members === 0;

  return (
    <div className="bg-card border border-border rounded-lg p-5 mb-8" data-testid="activation-analytics">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" />
          Activation Analytics
        </h3>
      </div>

      {noActivations ? (
        <div className="text-center py-4" data-testid="activation-empty-state">
          <p className="text-sm text-muted-foreground mb-3">
            No member activations yet. Invite teammates to get started.
          </p>
          <Button size="sm" variant="outline" onClick={onInviteClick} data-testid="activation-invite-btn">
            <UserPlus className="w-3.5 h-3.5 mr-1.5" />
            Invite Members
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Metrics row */}
          <div className="grid grid-cols-3 gap-4" data-testid="activation-metrics">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Activated</p>
              <p className="text-lg font-semibold" data-testid="activated-count">
                {data.activated_members} <span className="text-sm font-normal text-muted-foreground">/ {data.total_accepted_members}</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Activation Rate</p>
              <p className="text-lg font-semibold" data-testid="activation-rate">{pct}%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Median Time</p>
              <p className="text-lg font-semibold" data-testid="median-time">
                {data.median_time_to_activate_hours != null
                  ? `${data.median_time_to_activate_hours}h`
                  : "—"}
              </p>
            </div>
          </div>

          {/* Recent activations */}
          {data.last_activations.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Recent Activations</p>
              <div className="space-y-1.5">
                {data.last_activations.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm" data-testid={`recent-activation-${i}`}>
                    <span className="truncate">{a.member_name_or_email}</span>
                    <span className="text-xs text-muted-foreground flex-shrink-0 ml-2" title={a.activated_at}>
                      {timeAgo(a.activated_at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
